import { useAuth } from '@hb/react'
import {
  practiceContractConsentCheckboxes,
  practiceContractConsentHeader,
  practiceContractConsentPreface,
  PRACTICE_CONTRACT_AGREEMENTS,
  UserElectronicConsent,
} from '@hb/shared'
import { PropsWithChildren, useMemo } from 'react'
import { confirmPracticeContractAgreement } from '../../../backend/functions/practiceContracts'
import { useDocument } from '../../../backend/db/hooks/useDocument'
import {
  AgreementConfig,
  ElectronicConsentContext,
} from '../../Users/Profile/ConsentForms/ElectronicConsent/context'
import { ElectronicConsentModal } from '../../Users/Profile/ConsentForms/ElectronicConsent/ElectronicConsentModal'

// Provides the provider-facing practice-contract electronic-signature agreement into the shared
// ElectronicConsentContext, so the same overlay/modal gate that patients use also gates providers.
export const PracticeContractAgreementProvider = ({
  signerId,
  practiceId,
  children,
  modalOpen,
  setModalOpen,
}: PropsWithChildren<{
  signerId: string | null
  practiceId: string | null
  modalOpen: boolean
  setModalOpen: (open: boolean) => void
}>) => {
  const authUser = useAuth(s => s.authUser)
  const toBeSignedByMe = useMemo(
    () => !!signerId && !!authUser && signerId === authUser?.uid,
    [signerId, authUser],
  )

  const {
    data: consent,
    loading: consentLoading,
    error: consentLoadingError,
  } = useDocument<UserElectronicConsent>(PRACTICE_CONTRACT_AGREEMENTS, signerId)

  const sortedSubmissions = useMemo(() => {
    if (!consent) return null
    return Object.values(consent?.submissions ?? {}).sort(
      (a, b) => new Date(b.signedOn).getTime() - new Date(a.signedOn).getTime(),
    )
  }, [consent])

  const mostRecentSubmission = useMemo(() => sortedSubmissions?.[0] ?? null, [sortedSubmissions])

  const config = useMemo<AgreementConfig>(
    () => ({
      title: practiceContractConsentHeader,
      preface: practiceContractConsentPreface,
      checkboxes: practiceContractConsentCheckboxes,
      previewTitle: 'Practice Contract Agreement',
      signerFallbackLabel: 'provider',
      confirm: () =>
        confirmPracticeContractAgreement({
          signerId: signerId ?? '',
          practiceId: practiceId ?? '',
        }),
    }),
    [signerId, practiceId],
  )

  return (
    <ElectronicConsentContext.Provider
      value={{
        signerId,
        toBeSignedByMe,
        consent,
        sortedSubmissions,
        mostRecentSubmission,
        consentLoading,
        consentLoadingError,
        config,
      }}>
      {children}
      <ElectronicConsentModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </ElectronicConsentContext.Provider>
  )
}
